import React, { forwardRef } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import Button from '../../../../ui/components/Button';
import { GithubState, GithubData } from './GithubButton';
import { GithubSyncStatus } from '../../../../backend/useGithubRepo';
import SyncStatus from './SyncStatus';
import GithubRepo from './GithubRepo';
import Icon from '../../../../ui/global/Icon';

const Menu = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    min-width: 200px;
    max-width: 300px;
    border-radius: 6px;
    background-color: ${Colors.grey9};
    border: 1px solid ${Colors.grey8};
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 14px;
    line-height: 1.2em;
    color: ${Colors.grey1};
    padding: 10px 15px 15px 15px;
    gap: 10px;
`;

const TitleRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 100%;
    height: 28px;
    font-family: ${Fonts.lexendSemiBold.fontFamily};
    font-weight: ${Fonts.lexendSemiBold.fontWeight};
    font-size: 16px;
    gap: 10px;
`;

const ContentRow = styled.div<{ $maxWidth?: string }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: ${({ $maxWidth }) => $maxWidth};
    font-family: ${Fonts.lexendLight.fontFamily};
    font-weight: ${Fonts.lexendLight.fontWeight};
    font-size: 12px;
    line-height: 1.3em;
    color: ${Colors.grey2};
    gap: 10px;
`;


const ArrowBorder = styled.div`
    position: absolute;
    width: 0;
    height: 0;
    border-left: 11px solid transparent;
    border-right: 11px solid transparent;
    border-bottom: 11px solid ${Colors.grey8};
    top: -11px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    border-radius: 2px;
`;

const ArrowBody = styled.div`
    position: absolute;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid ${Colors.grey9};
    top: -9px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
    border-radius: 2px;
`;

interface GithubMenuProps {
    githubState: GithubState;
    status: GithubSyncStatus;
    onConnectClick: () => void;
    onCreateRepoClick: () => void;
    onRepoClick?: () => void;
    githubData: GithubData;
    retryMode?: boolean;
}

const GithubMenu = forwardRef<HTMLDivElement, GithubMenuProps>(({ 
    githubState,
    status,
    onConnectClick,
    onCreateRepoClick,
    onRepoClick,
    githubData,
    retryMode,
}, ref) => {

    switch (githubState) {
        case GithubState.Loading:
            return (
                <Menu ref={ref}>
                    <ArrowBorder />
                    <ArrowBody />
                    <TitleRow>
                        <span>Github</span>
                        <Icon icon="loading" size={18} color={Colors.white}/>
                    </TitleRow>
                </Menu>
            );
        case GithubState.ConnectAccount:
            return (
                <Menu ref={ref}>
                    <ArrowBorder />
                    <ArrowBody />
                    <TitleRow>
                        <span>Github</span>
                        <Button size="micro" color="white" text="Connect" onClick={onConnectClick} />
                    </TitleRow>
                    <ContentRow $maxWidth="200px">
                        <span>Connect your account to sync your code and manage version history.</span>
                    </ContentRow>
                </Menu>
            );
        case GithubState.CreateRepo:
            return (
                <Menu ref={ref}>
                    <ArrowBorder />
                    <ArrowBody />
                    <TitleRow>
                        <span>Github</span>
                        {retryMode && <SyncStatus status={status} />}
                        <Button size="micro" color="white" text={retryMode ? 'Retry' : 'Create'} onClick={onCreateRepoClick} />
                    </TitleRow>
                    <ContentRow>
                        <GithubRepo repoName={`${githubData.user_name}/${githubData.repo_name}`} />
                    </ContentRow>
                </Menu>
            );
        case GithubState.SyncRepo:
            return (
                <Menu ref={ref}>
                    <ArrowBorder />
                    <ArrowBody />
                    <TitleRow>
                        <span>Github</span>
                        <SyncStatus status={status} />
                    </TitleRow>
                    <ContentRow>
                        <GithubRepo repoName={`${githubData.user_name}/${githubData.repo_name}`} isButton={true} repoUrl={githubData.repo_link} />
                    </ContentRow>
                </Menu>
            );
        default:
            return null;
    }
});

export default GithubMenu;